import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { API, AssetCategoryFullDTO, Pagination } from '../../../../services/api/api';
import { ModalNavigationService } from '../../../../services/gui/modal/modal-navigation.service';
import { UtilService } from '../../../../services/util.service';
import { ModalNavigationComponent } from '../../../../services/gui/modal/modal-navigation-component';
import { finalize, take } from 'rxjs/operators';
import { ActivatedRoute, Params } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PermissionService } from '../../../../services/permission.service';
import { StringTransformationService } from '../../../../services/transformation-helper/string-transformation.service';
import { BehaviorSubject, Subscription } from 'rxjs';
import { SbSplitButtonItem, SearchOptions } from '@ngx-seatback/controls';
import { FilterPanelConfig } from '@ngx-seatback/layout';
import { SbSplitButton } from '../../../../services/global/global-interfaces';
import { RequestClass } from '../../../../services/global/RequestClass';

type SplitButtonAction = 'edit';

@Component({
  selector: 'sb-page-asset-category-list',
  templateUrl: './asset-category-list.component.html',
  styleUrls: ['./asset-category-list.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssetCategoryListComponent extends RequestClass implements OnInit, ModalNavigationComponent {
  public readonly modal = inject(ModalNavigationService);
  public readonly api = inject(API);
  public readonly route = inject(ActivatedRoute);
  public readonly util = inject(UtilService);
  protected readonly permissionService = inject(PermissionService);
  private readonly stringTransformation = inject(StringTransformationService);
  private readonly destroyRef = inject(DestroyRef);

  // global variables
  showSideBar: boolean = false;
  isInModalContext: boolean;
  repositoryId: number = -1;

  // table wrapper variables
  assetCategories: AssetCategoryFullDTO[] = [];
  selectedAssetCategory: AssetCategoryFullDTO;

  // filter variables
  exactMatch: SearchOptions = 'contains';
  search: string | null = null;
  filterConfig: FilterPanelConfig = {
    showExactMatch: true,
    showSearchHeadline: true,
    hideTagSection: true,
    button: { visibleWithoutChanges: true },
  };

  ngOnInit(): void {
    if (!this.isInModalContext) {
      this.route.params.subscribe((params: Params) => {
        this.repositoryId = params.repositoryID ? +params.repositoryID : null;
      });
    } else {
      this.repositoryId = this.modal.getCurrentNavigation().data.repoId;
    }
  }

  refreshFromServer() {
    if (!this.pagination) {
      return;
    }
    const sub = this.api
      .dm_assets_AssetRepository_getRepoCategories(
        this.repositoryId,
        {
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
        },
        this.pagination
      )
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: (assets: AssetCategoryFullDTO[]) => {
          this.assetCategories = assets;
          this.assetCategories = this.buildSplitButtons(this.assetCategories);
        },
      });
    this.subs.push(sub);
  }

  toggleSideBar() {
    this.showSideBar = !this.showSideBar;
  }

  addAssetCategory() {
    this.selectedAssetCategory = new AssetCategoryFullDTO();
    this.toggleSideBar();
  }

  editAssetCategory(assetCategory: AssetCategoryFullDTO) {
    this.selectedAssetCategory = assetCategory;
    this.toggleSideBar();
  }

  selectAssetCategory(assetCategory: AssetCategoryFullDTO) {
    if (this.isInModalContext) {
      this.modal.result(assetCategory);
    }
  }

  updateAssetCategory(assetCategory: AssetCategoryFullDTO) {
    if (!assetCategory.id) {
      this.api
        .dm_assets_AssetRepository_createAssetCategory(this.repositoryId, assetCategory)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.resetView();
          },
        });
    } else {
      this.api
        .dm_assets_AssetCategory_editAssetCategory(assetCategory.id, assetCategory)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.resetView();
          },
        });
    }
  }

  resetView() {
    this.toggleSideBar();
    this.selectedAssetCategory = null;
    this.requestDebounced();
  }

  buildSplitButtons<T extends { id: any }>(itemsToAdd: T[]): (T & SbSplitButton<SplitButtonAction>)[] {
    const result = itemsToAdd.map(item => {
      return { ...item, sbButtons: [] as SbSplitButtonItem<SplitButtonAction>[] };
    });
    result.forEach(item => {
      // TODO add Permission SBA-748
      item.sbButtons.push({
        icon: 'edit',
        action: 'edit',
        tooltip: 'lang.globals.details',
      });
    });
    return result;
  }

  resolveSplitButtonAction(assetCategory: AssetCategoryFullDTO, $event: SplitButtonAction) {
    switch ($event) {
      case 'edit':
        this.editAssetCategory(assetCategory);
        break;
      default:
        console.error('Missing action implementation of Splitbutton');
        break;
    }
  }

  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.requestDebounced();
  }

  setRequestOptions(filterMap: Map<string, any[]>) {
    this.search = filterMap.get('search')?.[0];
    this.exactMatch = filterMap.get('exactMatch')?.[0];
  }
}
